import { DropdownProps, MenuProps, Spin } from 'antd';
import { Key, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './index.scss';
import { useLocationData } from '@/hooks/useLocationData';
import { findLocationSyncModel } from '@/utils/locationUtils';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ROUTE_PATHS } from '@/constants/router.constants';
import ModalUpdateMerchant from './component/modal/modalUpdateMerchant';
import LocationContext, { StateData } from './component/hook/locationContext';
import ModalUpdateLocation from './component/modal/modalUpdateLocation';
import LocationTree from './component/locationTree';
import ToggleButton from './component/toggleButton';
import DeviceLocationMenu from './component/deviceLocationMenu';
import { LocationResponseModel } from '@/models/location';

function TerminalManagementForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isLoadingTree, locationData, treeData, fetchData } = useLocationData();
  const [treeItemSelected, setTreeItemSelected] = useState<LocationResponseModel | null>(null);
  const [open, setOpen] = useState(false);
  const [filteredItems, setFilteredItems] = useState<MenuProps['items']>([]);
  const [isModalMerchantOpen, setIsModalMerchantOpen] = useState(false);
  const [isModalLocationOpen, setIsModalLocationOpen] = useState(false);
  const location = useLocation();
  const [hasVisited, setHasVisited] = useState(true);
  const [hasDeleteRoot, setHasDeleteRoot] = useState(false);

  const [data, setData] = useState<StateData>({
    isLoading: false,
    isHiddenLocation: false,
    locationSelected: null,
    locationKey: ""
  });

  const reloadData = (shouldLoadFirst?: boolean) => {
    fetchData();

    if (shouldLoadFirst) {
      if (treeData.length == 1 && treeData[0].key == treeItemSelected?.key) {
        setHasDeleteRoot(false);
        setHasVisited(false);
        handleSelectTree([]);
        setData({
          isLoading: false,
          isHiddenLocation: false,
          locationSelected: null,
          locationKey: ""
        });
      }
      else {
        handleSelectTree([treeData[0]?.key]);
      }
    }
  };

  useEffect(() => {
    navigate(ROUTE_PATHS.TerminalManagement);
    setHasVisited(true);
  }, [hasDeleteRoot, hasVisited]);

  useEffect(() => {
    if(!treeItemSelected){
      handleSelectTree([treeData[0]?.key]);
    }
  }, [treeData]);

  useEffect(() => {
    if (hasVisited && treeItemSelected && location.pathname == ROUTE_PATHS.TerminalManagement) {
      navigate(
        ROUTE_PATHS.LocationDetail
          .replace(":parentId", treeItemSelected?.parentId ?? "")
          .replace(":id", treeItemSelected?.id ?? "")
      );
    }
  }, [location]);

  const handleSelectTree = (selectedKeys: Key[]) => {
    const key = selectedKeys.toString();
    const locationItem = key && findLocationSyncModel(locationData, key);
    if (key && locationItem) {
      setTreeItemSelected(locationItem || null);
      setData((prevData) => ({
        ...prevData,
        isLoading: !prevData.isLoading,
        locationSelected: locationItem,
        locationKey: key
      }));
      navigate(
        ROUTE_PATHS.LocationDetail
          .replace(":parentId", locationItem.parentId ?? "")
          .replace(":id", locationItem.id ?? "")
      );
    }
    else {
      setTreeItemSelected(locationItem || null);
    }
  };

  const toggleLocationTreeDisplay = () => {
    setData((prevData) => ({
      ...prevData,
      isHiddenLocation: !prevData.isHiddenLocation
    }));
  };

  const handleAddMerchant = () => {
    setIsModalMerchantOpen(true);
    setOpen(false);
  };

  const handleAddLocation = () => {
    setIsModalLocationOpen(true);
    setOpen(false);
  };

  const allItems: MenuProps['items'] = [
    { key: '1', label: 'Add Location', onClick: handleAddLocation },
    { key: '2', label: 'Add Sub Location', onClick: handleAddLocation },
    { key: '3', label: 'Add Merchant', onClick: handleAddMerchant },
  ];

  const handleOpenChange: DropdownProps['onOpenChange'] = (nextOpen, info) => {
    if (info.source === 'trigger' || nextOpen) {
      const filtered = treeItemSelected ? allItems.filter(item => item?.key !== '1') : allItems.filter(item => item?.key == '1');
      setFilteredItems(filtered);
      setOpen(nextOpen);
    }
  };

  const getKeySelectDefault = () => {
    if (!hasDeleteRoot && !hasVisited) {
      return treeData[0]?.key;
    }
    return hasDeleteRoot ? "" : treeItemSelected?.key ?? "";
  };
  
  const keySelectDefault = getKeySelectDefault();
  return (
    <LocationContext.Provider value={{ data, setData, reloadData }}>
      <div className="row-wrapper">
        <div className={`filter-col transaction-animation ${data.isHiddenLocation ? 'hidden' : ''}`}>
          <div className={`filter-left transaction-animation ${data.isHiddenLocation ? 'hidden' : ''}`}>
            <DeviceLocationMenu
              fetchData={fetchData}
              treeItemSelected={treeItemSelected}
              filteredItems={filteredItems}
              open={open}
              handleOpenChange={handleOpenChange}
            />
            <Spin spinning={isLoadingTree}>
              <LocationTree treeData={treeData} keySelectDefault={keySelectDefault} onSelectTree={handleSelectTree} />
            </Spin>
          </div>
          <ToggleButton
            isFilterHidden={data.isHiddenLocation}
            handleHideFilter={toggleLocationTreeDisplay}
            isStandby={data.isHiddenLocation}
          />

        </div>

        <div className="location-details transaction-animation">
          <div className="ctn-terminal-table">
            <Outlet />
          </div>
        </div>
        <ModalUpdateMerchant visible={isModalMerchantOpen} reloadLocationData={reloadData} onClose={() => setIsModalMerchantOpen(false)} parentItem={treeItemSelected} />
        <ModalUpdateLocation visible={isModalLocationOpen} reloadLocationData={reloadData} onClose={() => setIsModalLocationOpen(false)} parentItem={treeItemSelected} />
      </div>
    </LocationContext.Provider>
  );
}

export default TerminalManagementForm;
